import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import {
  dateToSections,
  formatSections,
  getDaysInMonth,
  getDefaultReference,
  getSegmentAtCaret,
  getSegmentRange,
  getSegments,
  parseFormat,
  parseTextIntoSections,
  type Sections,
  sectionsToDate,
  stepSegment,
  typeDigit,
  validate,
  type ValidationError,
} from './date-segments'
import { useChanged } from './use-changed'

export type UseDateSegmentsOptions = {
  value: Date | null
  format: string
  onChange?: (value: Date | null, context: { validationError: ValidationError | null }) => void
  min?: Date
  max?: Date
  /** Units the format does not carry are taken from here — defaults to `value`, then now. */
  referenceDate?: Date
  disabled?: boolean
  readOnly?: boolean
}

export type DateSegmentsApi = {
  inputRef: React.RefObject<HTMLInputElement | null>
  text: string
  validationError: ValidationError | null
  /** Re-derives the mask from `value` — the only way to clear a partial entry. */
  reset: () => void
  inputProps: {
    value: string
    inputMode: 'numeric'
    'aria-invalid': boolean | undefined
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
    onFocus: (event: React.FocusEvent<HTMLInputElement>) => void
    onMouseUp: (event: React.MouseEvent<HTMLInputElement>) => void
    onBlur: () => void
  }
}

/**
 * Binds the pure segment model to a single text input: caret snapping, digit
 * entry with auto-advance, and arrow-key stepping.
 *
 * `value` stays the source of truth, but sections are held locally too — a
 * half-typed date has no `Date` to round-trip through, so dropping to `value`
 * alone would erase the digits the user just entered.
 */
export function useDateSegments({
  value,
  format,
  onChange,
  min,
  max,
  referenceDate,
  disabled,
  readOnly,
}: UseDateSegmentsOptions): DateSegmentsApi {
  const parts = useMemo(() => parseFormat(format), [format])
  const segments = useMemo(() => getSegments(parts), [parts])

  const resolveReference = () => referenceDate ?? value ?? getDefaultReference(parts)

  const inputRef = useRef<HTMLInputElement>(null)
  const pendingRef = useRef('')
  const caretRef = useRef<number | null>(null)

  const [sections, setSections] = useState<Sections>(() => dateToSections(parts, value))
  const [activeSegment, setActiveSegment] = useState(0)
  const [touched, setTouched] = useState(false)

  // Sections are positional, so keeping them across a format change would
  // reinterpret e.g. day/month as hours/minutes.
  if (useChanged(parts)) {
    setSections(dateToSections(parts, value))
    setActiveSegment(0)
    pendingRef.current = ''
  }

  // Re-sync only when the incoming value genuinely differs from what the
  // sections already spell, so typing is never clobbered by the echoed onChange.
  const localDate = sectionsToDate(parts, sections, resolveReference())
  const localTime = localDate?.getTime() ?? null
  const valueTime = value?.getTime() ?? null

  // Adjusted during render rather than in an effect (the React "storing
  // information from previous renders" pattern): an effect would repaint the
  // stale mask first. Guarded on `localTime` so the value we just emitted does
  // not bounce back and wipe a half-typed date.
  //
  // A clear that leaves `value` at `null` is invisible here — the field has been
  // emitting `null` on every keystroke of a partial entry — so the owner calls
  // `reset()` for that instead.
  if (useChanged(valueTime) && localTime !== valueTime) {
    setSections(dateToSections(parts, value))
    pendingRef.current = ''
  }

  const text = formatSections(parts, sections)

  const validationError = validate(localDate, sections, { min, max })
  // Partial input is only surfaced as an error once the field has been left, so
  // the control does not flash red between the first and last keystroke.
  const visibleError = touched || validationError !== 'invalidDate' ? validationError : null

  const selectSegment = useCallback((index: number) => {
    caretRef.current = index
    setActiveSegment(index)
  }, [])

  useLayoutEffect(function restoreSegmentSelection() {
    const input = inputRef.current
    const index = caretRef.current

    if (!input || index == null || document.activeElement !== input) {
      return
    }

    const { start, end } = getSegmentRange(parts, sections, index)
    input.setSelectionRange(start, end)
    caretRef.current = null
  })

  const commit = useCallback(
    (next: Sections) => {
      setSections(next)

      const date = sectionsToDate(parts, next, referenceDate ?? value ?? getDefaultReference(parts))
      onChange?.(date, { validationError: validate(date, next, { min, max }) })
    },
    [parts, referenceDate, value, onChange, min, max],
  )

  /**
   * Applies one or more digits from the active segment onward, advancing as each
   * fills. Shared by physical keystrokes and by `beforeinput`, which is the only
   * signal soft keyboards and paste give us.
   */
  const applyDigits = (digits: string) => {
    const next = [...sections]
    let index = activeSegment
    let pending = pendingRef.current

    for (const digit of digits) {
      const segment = segments[index]

      if (!segment) {
        break
      }

      const result = typeDigit(segment.token, pending, digit)
      next[segment.index] = result.value
      pending = result.pending

      if (result.complete && index < segments.length - 1) {
        index += 1
        pending = ''
      }
    }

    pendingRef.current = pending
    commit(next)
    selectSegment(index)
  }

  const clearActiveSegment = () => {
    const segment = segments[activeSegment] ?? segments[0]

    if (!segment) {
      return
    }

    pendingRef.current = ''

    const next = [...sections]
    next[segment.index] = null

    commit(next)
    selectSegment(activeSegment)
  }

  /**
   * The authoritative text-entry path.
   *
   * `keydown` cannot be trusted on its own: Android soft keyboards report
   * `key: 'Unidentified'` for every character and for Backspace, so the guard in
   * `handleKeyDown` never matches and the raw edit used to reach `handleChange`,
   * which re-read the whole mask positionally — one Backspace turned 05/03/2026
   * into 03/20/0026. `beforeinput` carries `inputType` and `data` reliably across
   * input methods, so every insertion and deletion is routed through the segment
   * model here and the default edit is cancelled.
   */
  const handleBeforeInput = (event: Event) => {
    if (disabled === true || readOnly === true) {
      return
    }

    if (!(event instanceof InputEvent)) {
      return
    }

    const native = event

    if (native.inputType.startsWith('delete')) {
      event.preventDefault()
      setTouched(true)
      clearActiveSegment()

      return
    }

    if (native.inputType.startsWith('insert')) {
      event.preventDefault()
      setTouched(true)

      const data = native.data ?? ''

      // A whole value arriving at once — a paste, an autofill, a test driver's
      // `fill()` — carries its separators, and they mean something. Read as one
      // digit stream `1/8/2025` becomes 18/02/0025, because the day segment
      // greedily takes two digits; grouping on the separators keeps `1` and `8`
      // in their own segments. An unseparated run like `05032026` has no groups
      // to honour and still streams through `applyDigits`.
      if (/\d\D+\d/.test(data)) {
        commit(parseTextIntoSections(data, parts))
        selectSegment(Math.max(segments.length - 1, 0))
        pendingRef.current = ''

        return
      }

      const digits = data.replace(/\D/g, '')

      if (digits.length > 0) {
        applyDigits(digits)
      }
    }
  }

  // Re-read through a ref so the listener binds once but always sees current state.
  const beforeInputRef = useRef(handleBeforeInput)
  beforeInputRef.current = handleBeforeInput

  useEffect(function bindNativeBeforeInput() {
    const input = inputRef.current

    if (!input) {
      return undefined
    }

    const listener = (event: Event) => {
      beforeInputRef.current(event)
    }

    input.addEventListener('beforeinput', listener)

    return () => {
      input.removeEventListener('beforeinput', listener)
    }
  }, [])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled === true || readOnly === true || segments.length === 0) {
      return
    }

    const segment = segments[activeSegment] ?? segments[0]

    if (!segment) {
      return
    }

    const valueOf = (token: string) => {
      const found = segments.find((candidate) => candidate.token === token)

      return found ? (sections[found.index] ?? null) : null
    }
    const yearValue = valueOf('yyyy')
    const monthValue = valueOf('MM')

    if (/^\d$/.test(event.key)) {
      event.preventDefault()
      applyDigits(event.key)

      return
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      pendingRef.current = ''

      const fallbackSections = dateToSections(parts, referenceDate ?? new Date())
      const next = [...sections]
      next[segment.index] = stepSegment(
        segment.token,
        sections[segment.index] ?? null,
        event.key === 'ArrowUp' ? 1 : -1,
        fallbackSections[segment.index] ?? 0,
        segment.token === 'dd' ? getDaysInMonth(yearValue, monthValue) : undefined,
      )

      commit(next)
      selectSegment(activeSegment)

      return
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault()
      pendingRef.current = ''

      const delta = event.key === 'ArrowRight' ? 1 : -1
      selectSegment(Math.min(Math.max(activeSegment + delta, 0), segments.length - 1))

      return
    }

    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault()
      clearActiveSegment()

      return
    }

    // Anything else printable would reach the input, mutate the masked value and
    // be re-read positionally by `handleChange` — one stray `a` turns 05/03/2026
    // into 03/20/0026. Navigation and shortcut keys still pass through.
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault()
    }
  }

  // Last resort: a `change` with no preceding `beforeinput` — programmatic value
  // sets and `fireEvent.change` in consumer tests. Real user edits are handled
  // by `handleBeforeInput` before they get here.
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled === true || readOnly === true) {
      return
    }

    pendingRef.current = ''
    setTouched(true)
    commit(parseTextIntoSections(event.target.value, parts))
  }

  const syncSegmentFromCaret = (input: HTMLInputElement) => {
    const index = getSegmentAtCaret(parts, sections, input.selectionStart ?? 0)

    if (index != null) {
      selectSegment(index)
    }
  }

  return {
    inputRef,
    text,
    validationError: visibleError,
    reset: () => {
      setSections(dateToSections(parts, value))
      setActiveSegment(0)
      setTouched(false)
      pendingRef.current = ''
    },
    inputProps: {
      value: text,
      inputMode: 'numeric',
      'aria-invalid': visibleError ? true : undefined,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      // Tabbing in lands the caret at the end of the mask, which would start
      // typing in the year. Focus always opens on the first segment; a click
      // corrects it from the caret in `onMouseUp`, which fires afterwards.
      onFocus: () => {
        selectSegment(0)
      },
      onMouseUp: (event) => {
        syncSegmentFromCaret(event.currentTarget)
      },
      onBlur: () => {
        pendingRef.current = ''
        setTouched(true)
      },
    },
  }
}
